import { Head, Link } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatLocationTime } from "@/lib/location-time";
import { money } from "@/pages/jobs/types";
import { usePage } from "@inertiajs/react";
import SettingsLayout from "@/layouts/settings_layout";

type Job = {
  id: string;
  vehicle_id: string;
  work_state: string;
  billing_total_minor: string;
  payment_received_minor: string;
  outstanding_minor: string;
  assignee_name: string;
  representation: {
    customer: { name: string };
    vehicle: { make: string; model: string };
    total: { currency: string };
    schedule: { starts_at: string; time_zone: string };
  };
};
export default function LocationHistory({
  location_name: name,
  location_id: id,
  jobs,
  page,
  pages,
}: {
  location_name: string;
  location_id: string;
  jobs: Job[];
  page: number;
  pages: number;
}) {
  const { t } = useTranslation();
  const { i18n } = usePage().props;
  return (
    <SettingsLayout>
      <Head title={`${name} · ${t("resources.retained_history")}`} />
      <div className="space-y-4">
        <h1 className="text-page font-display">
          {name} · {t("resources.retained_history")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t("resources.history_help")}
        </p>
        {jobs.length === 0 && <p>{t("resources.no_jobs")}</p>}
        {jobs.map((job) => (
          <Card key={job.id}>
            <CardHeader>
              <CardTitle>
                {job.representation.customer.name} ·{" "}
                {job.representation.vehicle.make}{" "}
                {job.representation.vehicle.model}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                {formatLocationTime(
                  job.representation.schedule.starts_at,
                  { ...i18n, time_zone: job.representation.schedule.time_zone },
                  { dateStyle: "long", timeStyle: "short" },
                )}{" "}
                · {job.representation.schedule.time_zone}
              </p>
              <p>
                {t(`job_board.stages.${job.work_state}`, {
                  defaultValue: job.work_state,
                })}{" "}
                · {job.assignee_name}
              </p>
              <p>
                {t("resources.balance", {
                  total: money(
                    job.billing_total_minor,
                    job.representation.total.currency,
                    i18n.locale,
                  ),
                  received: money(
                    job.payment_received_minor,
                    job.representation.total.currency,
                    i18n.locale,
                  ),
                  outstanding: money(
                    job.outstanding_minor,
                    job.representation.total.currency,
                    i18n.locale,
                  ),
                })}
              </p>
              <Button
                variant="outline"
                nativeButton={false}
                render={
                  <Link href={`/${id}/vehicles/${job.vehicle_id}/history`} />
                }
              >
                {t("resources.retained_history")}
              </Button>
            </CardContent>
          </Card>
        ))}
        <nav
          className="flex gap-2"
          aria-label={t("resources.retained_history")}
        >
          {page > 1 && (
            <Button
              variant="outline"
              nativeButton={false}
              render={
                <Link
                  href={`/user/location_histories/${id}?page=${page - 1}`}
                />
              }
            >
              {t("customers.previous")}
            </Button>
          )}
          {page < pages && (
            <Button
              variant="outline"
              nativeButton={false}
              render={
                <Link
                  href={`/user/location_histories/${id}?page=${page + 1}`}
                />
              }
            >
              {t("customers.next")}
            </Button>
          )}
        </nav>
      </div>
    </SettingsLayout>
  );
}
